import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import "./ProfilePage.css";

const ProfilePage = ({ isDarkMode, isAdminView, toggleAdminView, toggleTheme }) => {
  const [theme, setTheme] = useState("light");
  const [role, setRole] = useState("User");
  const [userSettings, setUserSettings] = useState({
    username: "Guest User", // Default value for username
    email: "", // No email if not authenticated
    firstName: "Guest",
    lastName: "User",
    profileImg: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setTheme(isDarkMode ? "dark" : "light");
  }, [isDarkMode]);

  useEffect(() => {
    setRole(isAdminView ? "Admin" : "User");
  }, [isAdminView]);

  const navigate = useNavigate();

  const handleBackClick = () => {
    navigate(-1); // Navigate back to the previous page
  };

  const handleNotificationClick = () => {
    navigate("/notifications");
  };

  const renderProfileIcon = () => {
    if (userSettings.profileImg) {
      return <img src={userSettings.profileImg} alt="Profile" className="profile-img" />;
    }
    const initials = `${userSettings.firstName[0]}${userSettings.lastName[0]}`;
    return <div className="profile-initials">{initials}</div>;
  };

  const handleLogout = () => {
    document.cookie = "token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    document.cookie = "username=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    window.location.href = 'https://infia.enviseer.com/';
  };

  const fetchUserSettings = useCallback(async () => {
    setLoading(true);
    setError(null);

    function getCookie(name) {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop().split(';').shift();
    }

    const token = getCookie('token');
    const username = getCookie('username');

    if (!token || !username) {
      setUserSettings({
        username: "Guest",
        email: "",
        firstName: "Guest",
        lastName: "User",
        profileImg: null,
      });
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`https://infia.enviseer.com/api/api/user/fetchUser?email=${username}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'X-Application-Name': 'envi-onboarding'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          // handleLogout(); // Uncomment if you want to handle logout on 401
        } else {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
      }

      const data = await response.json();
      setUserSettings({
        username: data.displayName || "Guest",
        email: data.email || "",
        firstName: data.firstName || "Guest",
        lastName: data.lastName || "",
        profileImg: data.profileImg || null,
      });
    } catch (error) {
      console.error("Error fetching user settings:", error);
      setError(error.message);
      setUserSettings({
        username: "Guest",
        email: "",
        firstName: "Guest",
        lastName: "",
        profileImg: null,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUserSettings();
  }, [fetchUserSettings]);

  return (
    <div className={`profile-page ${theme}`}>
      {/* Top Bar */}
      <div className="top-bar">
        <button className="back-button" onClick={handleBackClick}>
          &lt; Back
        </button>
        <h1 className="top-heading">Profile</h1>
        <div className="notification-icon" onClick={handleNotificationClick}>
          <i className="bi bi-bell-fill"></i>
        </div>
      </div>

      {/* Profile Section */}
      <div className="profile-section">
        {renderProfileIcon()}
        <h2>{`${userSettings.firstName} ${userSettings.lastName}`}</h2>
        {userSettings.email && <p>{userSettings.email}</p>}
      </div>

      {/* Account Settings */}
      <div className="account-settings">
        <h3>Account Settings</h3>

        {/* Account Type Dropdown */}
        <div className="setting-row">
          <span>Account Type</span>
          <select value={role} onChange={toggleAdminView} style={{ backgroundColor: '#4a47a3' }}>
            <option value="User">User</option>
            <option value="Admin" className="admin-option">Admin</option>
          </select>
        </div>

        {/* Theme Setting */}
        <div className="setting-row">
          <span>Theme Setting</span>
          <div className={`icon-toggle ${theme}`} onClick={toggleTheme}>
            <div className="toggle-icon">
              {theme === "light" ? (
                <i className="bi bi-brightness-high-fill"></i>
              ) : (
                <i className="bi bi-moon-fill"></i>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Logout Button */}
      <div className="logout-container">
        <button className="logout-btn" onClick={handleLogout}>Log Out</button>
      </div>
    </div>
  );
};

ProfilePage.propTypes = {
  isAdminView: PropTypes.bool.isRequired,
  isDarkMode: PropTypes.bool.isRequired,
  toggleAdminView: PropTypes.func.isRequired,
  toggleTheme: PropTypes.func.isRequired,
};

export default ProfilePage;
